"use client";

import { Activity, ArrowRight, ChartBar as BarChart3, CircleCheck as CheckCircle2, Phone, TrendingUp, Users, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function AnalyticsReporting() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: visualRef,
    isVisible: visualVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const statChips = [{
    icon: Activity,
    title: "100% Real-time reporting",
    description: "Instant data synchronization across all channels without delay."
  }, {
    icon: BarChart3,
    title: "12+ Metrics tracked",
    description: "From AI search positioning down to call conversion rates."
  }, {
    icon: CheckCircle2,
    title: "1 Unified dashboard",
    description: "All client growth metrics in one plain-English interface."
  }];
  return <section data-section-id="5162" id="analyticsreporting" className="relative overflow-hidden bg-background py-20 sm:py-28 lg:py-32 text-foreground">
      {}
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[500px] -translate-x-1/2 rounded-lg opacity-30 blur-3xl" style={{
      background: "radial-gradient(circle, var(--accent-deep-red) 0%, transparent 70%)"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {}
          <div ref={headerRef} className={`lg:col-span-6 transition-all duration-700 ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-card/50 px-4 py-1.5 backdrop-blur-sm">
              <BarChart3 className="h-4 w-4" style={{
              color: "var(--accent-deep-red)"
            }} aria-hidden="true" />
              <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">
                ANALYTICS &amp; REPORTING
              </span>
            </div>

            <h1 className="font-default mt-6 text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl lg:text-6xl leading-[1.1]">
              Turn raw data into{" "}
              <br className="hidden sm:inline" />
              <span className="bg-clip-text text-transparent" style={{
              backgroundImage: "linear-gradient(135deg, var(--accent-deep-red) 0%, hsl(352 54% 45%) 50%, var(--accent-deep-red) 100%)"
            }}>
                decisions that grow revenue.
              </span>
            </h1>

            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl">
              Stop guessing which marketing channels actually move the needle. ClearLine provides plain-English analytics tracking local AI visibility, incoming phone calls, and booked jobs in real time.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild className="bg-[var(--accent-deep-red)] text-white hover:bg-[var(--accent-deep-red)]/90 h-11 px-6 shadow-md rounded-lg font-medium">
                <Link className="underline cursor-pointer" to="/PricingPlansPage">
                  <span>Get started now</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-border hover:bg-accent/50 h-11 px-6 rounded-lg font-medium">
                <Link to="/VisibilityCheckupPage">Free AI Visibility Checkup</Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={visualRef} className={`lg:col-span-6 transition-all duration-1000 ${visualVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            <div className="relative rounded-2xl border border-border bg-card/80 p-6 sm:p-8 backdrop-blur-md shadow-xl overflow-hidden">
              {}
              <div className="pointer-events-none absolute -right-20 -bottom-20 h-64 w-64 rounded-lg opacity-20 blur-3xl" style={{
              background: "radial-gradient(circle, var(--accent-deep-red) 0%, transparent 70%)"
            }} aria-hidden="true" />

              {}
              <div className="flex items-center justify-between pb-6 border-b border-border/60">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--accent-deep-red)]/15 text-[var(--accent-deep-red)]">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-foreground">
                      Revenue &amp; Lead Pulse
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Real-time aggregated stream
                    </p>
                  </div>
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background/60 px-2.5 py-1 text-xs font-mono text-muted-foreground">
                  <span className="h-2 w-2 rounded-lg bg-emerald-500 animate-pulse" />
                  Live Feed
                </div>
              </div>

              {}
              <div className="mt-6">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    Conversion Velocity
                  </span>
                  <span className="text-sm font-bold text-[var(--accent-deep-red)]">
                    +38% vs last month
                  </span>
                </div>

                <div className="relative h-44 w-full">
                  <svg className="h-full w-full overflow-visible" viewBox="0 0 400 140" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--accent-deep-red)" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="var(--accent-deep-red)" stopOpacity="0" />
                      </linearGradient>
                    </defs>

                    {}
                    <path d="M 0,110 Q 70,85 130,95 T 260,40 T 400,15 L 400,140 L 0,140 Z" fill="url(#chartGradient)" />

                    {}
                    <path d="M 0,110 Q 70,85 130,95 T 260,40 T 400,15" fill="none" stroke="var(--accent-deep-red)" strokeWidth="3" strokeLinecap="round" />

                    {}
                    <circle cx="260" cy="40" r="5" fill="var(--accent-deep-red)" />
                    <circle cx="400" cy="15" r="5" fill="var(--accent-deep-red)" className="animate-ping" />
                    <circle cx="400" cy="15" r="5" fill="var(--accent-deep-red)" />
                  </svg>
                </div>
              </div>

              {}
              <div className="mt-6 grid grid-cols-3 gap-3 pt-6 border-t border-border/60">
                <div className="rounded-lg border border-border/80 bg-background/40 p-3 shadow-sm">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Phone className="h-3.5 w-3.5 text-[var(--accent-deep-red)]" />
                    <span>Calls</span>
                  </div>
                  <p className="mt-1 text-lg font-bold text-foreground">142</p>
                  <span className="text-[11px] font-medium text-emerald-400">
                    +24% MoM
                  </span>
                </div>

                <div className="rounded-lg border border-border/80 bg-background/40 p-3 shadow-sm">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Users className="h-3.5 w-3.5 text-[var(--accent-deep-red)]" />
                    <span>Leads</span>
                  </div>
                  <p className="mt-1 text-lg font-bold text-foreground">89</p>
                  <span className="text-[11px] font-medium text-emerald-400">
                    +38% MoM
                  </span>
                </div>

                <div className="rounded-lg border border-border/80 bg-background/40 p-3 shadow-sm">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Zap className="h-3.5 w-3.5 text-[var(--accent-deep-red)]" />
                    <span>Conv. Rate</span>
                  </div>
                  <p className="mt-1 text-lg font-bold text-foreground">18.4%</p>
                  <span className="text-[11px] font-medium text-emerald-400">
                    +5.2% MoM
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={statsRef} className={`mt-16 grid grid-cols-1 gap-4 sm:grid-cols-3 lg:gap-6 transition-all duration-1000 ${statsVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          {statChips.map((chip, i) => {
          const ChipIcon = chip.icon;
          return <div key={i} data-index={i} className="group flex items-center gap-4 rounded-xl border border-border bg-card/80 p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[var(--accent-deep-red)]/40 hover:shadow-md">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] group-hover:bg-[var(--accent-deep-red)]/20 transition-colors">
                  <ChipIcon className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-base font-semibold text-foreground">
                    {chip.title}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground leading-normal">
                    {chip.description}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}